import { computed, effect, Injectable, signal, untracked } from '@angular/core';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SseService } from 'src/app/services/sse.service';
import { AuthStore } from 'src/app/store/auth.store';
import { API } from 'src/app/services/constants';
import {
  AgentStatus,
  AttachmentKind,
  ChatMessage,
  ChatMessageStatus,
  ChatSender,
  Conversation,
  ConversationStatus,
  SupportChatStreamEvent
} from '../support-chat.model';
import { SupportChatService } from './support-chat.service';

const TYPING_TIMEOUT_MS = 6_000;

@Injectable({ providedIn: 'root' })
export class SupportChatStore {
  // ── state ──────────────────────────────────────────────
  messages = signal<ChatMessage[]>([]);
  loading = signal(false);
  sending = signal(false);
  hasMore = signal(true);
  unreadCount = signal(0);
  hasConversation = signal(false);
  adminTyping = signal(false);
  lastAdminActivityAt = signal<Date | null>(null);
  // Manual agent presence status set by the support bot via /active and
  // /inactive. When `inactive` the UI swaps the green presence dot for a
  // moon icon. Defaults to `active` until the backend tells us otherwise.
  adminStatus = signal<AgentStatus>('active');
  initialLoaded = signal(false);
  conversation = signal<Conversation | null>(null);
  startingNewConversation = signal(false);
  // Controls the floating chat widget rendered globally in app.component.
  // External callers (e.g. the "Continuar conversación" CTA in fund-account)
  // flip this to open the chat without needing to navigate to a dedicated page.
  widgetOpen = signal(false);

  // True when the customer is actively looking at the message thread (entered
  // the conversation view, not just sitting on the welcome screen). The
  // SupportChatComponent keeps this in sync. Used by handleIncoming to flip
  // freshly-arrived admin messages straight to READ instead of leaving them
  // at DELIVERED, since the user already saw them.
  //
  // Each effective change is also pushed to the backend so the support bot's
  // /status command can report "chat abierto: sí/no" — see pushViewerState.
  viewerInThread = signal(false);
  private lastPostedViewerOpen: boolean | null = null;
  private visibilityListenerInstalled = false;

  setViewerInThread(active: boolean) {
    this.viewerInThread.set(active);
    this.installVisibilityListenerOnce();
    void this.pushViewerState();
  }

  private installVisibilityListenerOnce() {
    if (this.visibilityListenerInstalled) return;
    if (typeof document === 'undefined') return;
    document.addEventListener('visibilitychange', () => {
      if (!this.viewerInThread()) return;
      void this.pushViewerState();
    });
    this.visibilityListenerInstalled = true;
  }

  private async pushViewerState() {
    const effectiveOpen = this.viewerInThread() && this.documentIsVisible();
    if (this.lastPostedViewerOpen === effectiveOpen) return;
    this.lastPostedViewerOpen = effectiveOpen;
    try {
      await this.chatService.setViewerState(effectiveOpen);
    } catch {
      // best-effort; backend will fall back to staleness check anyway
      this.lastPostedViewerOpen = null;
    }
  }

  openWidget() {
    this.widgetOpen.set(true);
  }

  closeWidget() {
    this.widgetOpen.set(false);
  }

  toggleWidget() {
    this.widgetOpen.update((v) => !v);
  }

  private documentIsVisible(): boolean {
    return typeof document === 'undefined' || document.visibilityState === 'visible';
  }

  readonly adminOnline = computed(() => {
    const ts = this.lastAdminActivityAt();
    if (!ts) return false;
    const windowMs = 5 * 60 * 1000;
    return Date.now() - ts.getTime() < windowMs;
  });

  readonly conversationClosed = computed(
    () => this.conversation()?.status === ConversationStatus.CLOSED
  );

  // ── private ────────────────────────────────────────────
  private subscription: Subscription | null = null;
  private typingTimer: ReturnType<typeof setTimeout> | null = null;
  private typingNotifyAt = 0;
  private streamStarted = false;
  // FIFO of localIds of optimistic customer messages awaiting persistence.
  // Used to reconcile race between HTTP response and SSE event.
  private pendingLocalIds: string[] = [];
  // SSE reconnect bookkeeping: when we lose connection and reconnect, we may
  // have missed messages — fetch the latest page and merge by _id.
  private currentlyConnected = false;
  private hasConnectedOnce = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private chatService: SupportChatService,
    private sse: SseService,
    private authStore: AuthStore
  ) {
    // React to auth changes: fetch unread count (for badge) on login,
    // clear state on logout. The SSE stream is started lazily by
    // ensureStream() only when the chat widget/view is actually opened.
    effect(() => {
      const user = this.authStore.user();
      if (user) {
        untracked(() => this.refreshUnreadCount());
      } else {
        untracked(() => this.clearStore());
      }
    });
  }

  // Wipes all session-scoped state so a logged-out user (or the next user
  // signing in) does not see the previous conversation's history.
  clearStore() {
    this.disconnect();
    this.resetMessageState();
    this.conversation.set(null);
    this.startingNewConversation.set(false);
    this.hasConnectedOnce = false;
  }

  // Soft reset for "Iniciar nueva conversación": clears the visible thread
  // and per-conversation counters but keeps the SSE connection alive.
  private resetMessageState() {
    this.messages().forEach((m) => this.revokeOptimisticPreviews(m));
    this.messages.set([]);
    this.loading.set(false);
    this.sending.set(false);
    this.hasMore.set(true);
    this.unreadCount.set(0);
    this.hasConversation.set(false);
    this.adminTyping.set(false);
    this.lastAdminActivityAt.set(null);
    this.adminStatus.set('active');
    this.initialLoaded.set(false);
    this.pendingLocalIds = [];
    this.typingNotifyAt = 0;
  }

  // ── stream management ──────────────────────────────────
  ensureStream() {
    if (this.streamStarted) return;
    this.streamStarted = true;
    this.refreshUnreadCount();
    this.connect();
  }

  private connect() {
    this.disconnect();
    const url = `${environment.api_url}/${API.supportChatStream}`;
    this.subscription = this.sse.connect(url).subscribe({
      next: (event: SupportChatStreamEvent) => {
        if (!this.currentlyConnected) {
          this.currentlyConnected = true;
          // On reconnect (not the very first connect), pull recent history
          // in case any message was emitted while we were offline.
          if (this.hasConnectedOnce) void this.resyncRecent();
          this.hasConnectedOnce = true;
        }
        this.handleStreamEvent(event);
      },
      error: () => {
        this.currentlyConnected = false;
        // SSE service closes on error; retry after 5s
        this.retryTimer = setTimeout(() => this.connect(), 5_000);
      }
    });
  }

  // Pulls the most recent slice of messages and merges any unseen ones into
  // the head of `messages`. Used after an SSE reconnect to recover gaps. Also
  // re-fetches the unread count so the badge is consistent with messages the
  // SSE may have missed while disconnected.
  private async resyncRecent() {
    void this.refreshUnreadCount();
    if (!this.initialLoaded()) return;
    try {
      const res = await this.chatService.list({ limit: 20 });
      if (!res.data) return;
      const incoming = res.data.messages ?? [];
      if (incoming.length === 0) return;
      this.messages.update((prev) => {
        const known = new Set(
          prev.map((m) => (m._id ?? m.id) as string | undefined).filter(Boolean) as string[]
        );
        const newest = incoming.filter((m) => {
          const id = (m._id ?? m.id) as string | undefined;
          return id && !known.has(id);
        });
        if (newest.length === 0) return prev;
        return [...newest, ...prev];
      });
      const ts = res.data.conversation?.lastAdminActivityAt;
      if (ts) this.lastAdminActivityAt.set(new Date(ts));
    } catch {
      // best-effort; SSE will eventually deliver anyway
    }
  }

  disconnect() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.streamStarted = false;
    this.currentlyConnected = false;
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
      this.typingTimer = null;
    }
  }

  private handleStreamEvent(event: SupportChatStreamEvent) {
    if (!event) return;
    if (event.type !== 'heartbeat' && !environment.production) {
      // eslint-disable-next-line no-console
      console.log('[support-chat] SSE event', event);
    }
    switch (event.type) {
      case 'message':
        this.handleIncoming(event.message);
        break;
      case 'typing':
        this.setAdminTyping();
        break;
      case 'read':
        this.applyReadUpTo(event.untilMessageId);
        break;
      case 'presence':
        if (event.online) this.lastAdminActivityAt.set(new Date());
        if (event.status) this.adminStatus.set(event.status);
        break;
      case 'conversation':
        this.handleConversationUpdate(event.conversation);
        break;
      case 'delete': {
        const known = this.messages().find(
          (m) => (m._id ?? m.id) === event.messageId
        );
        if (!environment.production) {
          // eslint-disable-next-line no-console
          console.log(
            '[support-chat] delete event',
            event.messageId,
            known ? 'MATCHED → removing' : 'NO MATCH in current messages'
          );
        }
        this.removeMessageLocally(event.messageId);
        break;
      }
      case 'heartbeat':
        // noop
        break;
    }
  }

  private handleConversationUpdate(conv: Conversation) {
    if (!conv) return;
    const previous = this.conversation();
    const previousId = previous?._id ?? previous?.id;
    const incomingId = conv._id ?? conv.id;

    // Same conversation, different status (e.g. closed by admin).
    if (previousId && incomingId && previousId === incomingId) {
      this.conversation.set(conv);
      return;
    }

    // A brand-new conversation replaced the old one (started from another
    // tab, or from this tab via startNewConversation). Reset the visible
    // thread to match the new state.
    this.resetMessageState();
    this.conversation.set(conv);
    this.initialLoaded.set(true);
  }

  private handleIncoming(message: ChatMessage) {
    if (!message) return;
    const id = message._id ?? message.id;
    if (!id) return;

    // Already in the list — treat as an update (edited text, replyTo loaded
    // later, status promotion, etc.) instead of dropping the event. We
    // merge so optimistic fields (localId, localPreviewUrl on attachments)
    // are preserved when the payload from the server doesn't include them.
    const existingIdx = this.messages().findIndex((m) => (m._id ?? m.id) === id);
    if (existingIdx !== -1) {
      this.messages.update((prev) => {
        const next = [...prev];
        next[existingIdx] = { ...next[existingIdx], ...message };
        return next;
      });
      return;
    }

    // Customer message arriving via SSE while an optimistic copy is still
    // pending its HTTP response → replace the oldest pending optimistic
    // in FIFO order instead of inserting a duplicate.
    if (message.sender === ChatSender.CUSTOMER && this.pendingLocalIds.length > 0) {
      const targetLocalId = this.pendingLocalIds.shift()!;
      this.messages.update((prev) => {
        const idx = prev.findIndex((m) => m.localId === targetLocalId);
        if (idx === -1) return [message, ...prev];
        this.revokeOptimisticPreviews(prev[idx]);
        const next = [...prev];
        next[idx] = message;
        return next;
      });
      return;
    }

    this.messages.update((prev) => [message, ...prev]);
    this.hasConversation.set(true);
    if (message.sender === ChatSender.ADMIN) {
      this.lastAdminActivityAt.set(new Date());
      this.unreadCount.update((c) => c + 1);
      this.adminTyping.set(false);
      // Customer is already inside the thread with the tab focused → they're
      // literally watching the message arrive. Fire markRead so Telegram
      // jumps from DELIVERED to READ without making them touch anything.
      if (this.viewerInThread() && this.documentIsVisible()) {
        void this.markRead();
      }
    }
  }

  private applyReadUpTo(untilMessageId?: string) {
    this.messages.update((prev) => {
      // Messages are stored newest-first. Find the boundary message so we
      // only promote messages up to (and including) it. If the id is missing
      // or not found, fall back to marking all customer messages as READ.
      let boundaryIdx = -1;
      if (untilMessageId) {
        boundaryIdx = prev.findIndex(
          (m) => (m._id ?? m.id) === untilMessageId
        );
      }
      return prev.map((m, i) => {
        if (m.sender !== ChatSender.CUSTOMER) return m;
        if (m.status === ChatMessageStatus.READ) return m;
        // If boundary found, only mark messages at boundaryIdx or later (older)
        if (boundaryIdx !== -1 && i < boundaryIdx) return m;
        return { ...m, status: ChatMessageStatus.READ };
      });
    });
  }

  private setAdminTyping() {
    this.adminTyping.set(true);
    if (this.typingTimer) clearTimeout(this.typingTimer);
    this.typingTimer = setTimeout(() => {
      this.adminTyping.set(false);
      this.typingTimer = null;
    }, TYPING_TIMEOUT_MS);
  }

  // ── data fetching ──────────────────────────────────────
  async loadInitial() {
    this.loading.set(true);
    try {
      const res = await this.chatService.list({ limit: 50 });
      if (res.data) {
        this.messages.set(res.data.messages ?? []);
        this.hasMore.set(res.data.hasMore ?? false);
        this.conversation.set(res.data.conversation ?? null);
        this.lastAdminActivityAt.set(
          res.data.conversation?.lastAdminActivityAt
            ? new Date(res.data.conversation.lastAdminActivityAt)
            : null
        );
        this.unreadCount.set(res.data.conversation?.unreadByCustomer ?? 0);
        if ((res.data.messages ?? []).length > 0) this.hasConversation.set(true);
        this.initialLoaded.set(true);
      }
    } finally {
      this.loading.set(false);
    }
  }

  async startNewConversation() {
    if (this.startingNewConversation()) return;
    this.startingNewConversation.set(true);
    try {
      const res = await this.chatService.startNewConversation();
      if (res.data) {
        // Clear visible thread and apply the new conversation immediately.
        // We don't wait for the SSE 'conversation' event because the SSE
        // path is a multi-tab safety net, not the primary mechanism.
        this.resetMessageState();
        this.conversation.set(res.data);
        this.initialLoaded.set(true);
      }
    } finally {
      this.startingNewConversation.set(false);
    }
  }

  async loadMore() {
    if (!this.hasMore() || this.loading()) return;
    const oldest = this.messages()[this.messages().length - 1];
    if (!oldest) return;
    this.loading.set(true);
    try {
      const res = await this.chatService.list({
        before: oldest.createdAt,
        beforeId: oldest._id ?? oldest.id,
        limit: 50
      });
      if (res.data) {
        this.messages.update((prev) => [...prev, ...(res.data!.messages ?? [])]);
        this.hasMore.set(res.data.hasMore ?? false);
      }
    } finally {
      this.loading.set(false);
    }
  }

  async refreshUnreadCount() {
    const res = await this.chatService.unreadCount();
    if (res.data) {
      this.unreadCount.set(res.data.count ?? 0);
      this.hasConversation.set(!!res.data.hasMessages);
    }
  }

  // ── sending ────────────────────────────────────────────
  async send(text: string | undefined, files: File[] = [], audioDurationSec?: number) {
    const trimmed = text?.trim();
    if (!trimmed && files.length === 0) return;
    const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const optimistic: ChatMessage = {
      localId,
      sender: ChatSender.CUSTOMER,
      text: trimmed,
      status: ChatMessageStatus.SENDING,
      createdAt: new Date().toISOString(),
      attachments:
        files.length > 0
          ? files.map((f) => {
              const isImage = f.type.startsWith('image/');
              const isAudio = f.type.startsWith('audio/');
              const kind = isImage
                ? AttachmentKind.IMAGE
                : isAudio
                  ? AttachmentKind.AUDIO
                  : AttachmentKind.DOCUMENT;
              return {
                kind,
                minioObjectKey: '',
                mimeType: f.type,
                sizeBytes: f.size,
                originalName: f.name,
                // For audio we keep the blob URL so the recipient bubble can
                // play it back instantly (before the upload finishes / before
                // the SSE-emitted final message comes back with the MinIO
                // object key).
                localPreviewUrl:
                  isImage || isAudio ? URL.createObjectURL(f) : undefined,
                durationSec: isAudio ? audioDurationSec : undefined
              };
            })
          : undefined,
      // Retained until the message is confirmed sent — used by retry() when
      // the upload fails halfway through.
      pendingFiles: files.length > 0 ? files : undefined,
      pendingAudioDurationSec: audioDurationSec
    };
    this.messages.update((prev) => [optimistic, ...prev]);
    this.hasConversation.set(true);
    this.pendingLocalIds.push(localId);
    this.sending.set(true);

    const consumePending = () => {
      const idx = this.pendingLocalIds.indexOf(localId);
      if (idx !== -1) this.pendingLocalIds.splice(idx, 1);
    };

    try {
      const res = await this.chatService.send(trimmed, files, audioDurationSec);
      if (res.data) {
        const persisted = res.data;
        consumePending();
        this.messages.update((prev) => {
          // If the optimistic is still there (SSE hasn't replaced it yet) → swap it.
          // If it's gone (SSE already inserted/replaced) → only insert if no message
          // with the persisted _id already exists.
          const idx = prev.findIndex((m) => m.localId === localId);
          if (idx !== -1) {
            this.revokeOptimisticPreviews(prev[idx]);
            const next = [...prev];
            next[idx] = persisted;
            return next;
          }
          const pid = (persisted as any)._id ?? (persisted as any).id;
          if (pid && prev.some((m) => (m._id ?? m.id) === pid)) return prev;
          return [persisted, ...prev];
        });
      } else {
        consumePending();
        this.messages.update((prev) =>
          prev.map((m) =>
            m.localId === localId ? { ...m, status: ChatMessageStatus.FAILED } : m
          )
        );
      }
    } catch {
      consumePending();
      this.messages.update((prev) =>
        prev.map((m) =>
          m.localId === localId ? { ...m, status: ChatMessageStatus.FAILED } : m
        )
      );
    } finally {
      this.sending.set(false);
    }
  }

  private revokeOptimisticPreviews(message: ChatMessage | undefined) {
    message?.attachments?.forEach((a) => {
      if (a.localPreviewUrl) {
        URL.revokeObjectURL(a.localPreviewUrl);
      }
    });
  }

  // Retries a previously-failed customer message. Reuses the File refs and
  // duration kept on the optimistic record so the user doesn't have to
  // re-pick / re-record. No-op if the message isn't FAILED or doesn't have
  // pending payload (e.g. an older message from a previous session that the
  // SSE backfill restored without local file refs).
  async retry(messageId: string): Promise<boolean> {
    const target = this.messages().find(
      (m) => m.localId === messageId || m._id === messageId || m.id === messageId
    );
    if (!target) return false;
    if (target.status !== ChatMessageStatus.FAILED) return false;
    const text = target.text;
    const files = target.pendingFiles ?? [];
    const duration = target.pendingAudioDurationSec;
    if (!text?.trim() && files.length === 0) return false;

    // Flip back to SENDING optimistically while the new attempt is in flight.
    // The localId we use here is the same as before so the FIFO ordering of
    // pendingLocalIds in handleIncoming stays consistent.
    const localId = target.localId ?? `local-${Date.now()}-retry`;
    this.messages.update((prev) =>
      prev.map((m) =>
        m === target
          ? {
              ...m,
              localId,
              status: ChatMessageStatus.SENDING,
              createdAt: new Date().toISOString()
            }
          : m
      )
    );
    this.pendingLocalIds.push(localId);
    this.sending.set(true);

    const consumePending = () => {
      const idx = this.pendingLocalIds.indexOf(localId);
      if (idx !== -1) this.pendingLocalIds.splice(idx, 1);
    };

    try {
      const res = await this.chatService.send(text, files, duration);
      if (res.data) {
        const persisted = res.data;
        consumePending();
        this.messages.update((prev) => {
          const idx = prev.findIndex((m) => m.localId === localId);
          if (idx !== -1) {
            this.revokeOptimisticPreviews(prev[idx]);
            const next = [...prev];
            next[idx] = persisted;
            return next;
          }
          const pid = (persisted as any)._id ?? (persisted as any).id;
          if (pid && prev.some((m) => (m._id ?? m.id) === pid)) return prev;
          return [persisted, ...prev];
        });
        return true;
      }
      consumePending();
      this.markFailed(localId);
      return false;
    } catch {
      consumePending();
      this.markFailed(localId);
      return false;
    } finally {
      this.sending.set(false);
    }
  }

  private markFailed(localId: string) {
    this.messages.update((prev) =>
      prev.map((m) =>
        m.localId === localId ? { ...m, status: ChatMessageStatus.FAILED } : m
      )
    );
  }

  notifyTyping() {
    const now = Date.now();
    if (now - this.typingNotifyAt < 2_000) return;
    this.typingNotifyAt = now;
    this.chatService.notifyTyping().catch(() => undefined);
  }

  // ── edit / delete ──────────────────────────────────────
  async editMessage(messageId: string, newText: string): Promise<boolean> {
    try {
      const res = await this.chatService.editMessage(messageId, newText);
      if (!res?.data) return false;
      const updated = res.data;
      this.messages.update((prev) =>
        prev.map((m) => ((m._id ?? m.id) === messageId ? { ...m, ...updated } : m))
      );
      return true;
    } catch {
      return false;
    }
  }

  async deleteMessage(messageId: string): Promise<boolean> {
    try {
      const res = await this.chatService.deleteMessage(messageId);
      if (!res?.data) return false;
      this.removeMessageLocally(messageId);
      return true;
    } catch {
      return false;
    }
  }

  private removeMessageLocally(messageId: string) {
    this.messages.update((prev) => {
      const idx = prev.findIndex((m) => (m._id ?? m.id) === messageId);
      if (idx === -1) return prev;
      this.revokeOptimisticPreviews(prev[idx]);
      const next = [...prev];
      next.splice(idx, 1);
      return next;
    });
  }

  // In-session dedupe so a customer scrubbing/replaying the same voice note
  // doesn't hammer the bot with reaction calls. Telegram's setMessageReaction
  // is idempotent so the BE is safe regardless, but skipping the round-trip
  // saves both an HTTP hop and a Telegram API call per replay.
  private audioPlayedReported = new Set<string>();

  async markAudioPlayed(messageId: string | undefined) {
    if (!messageId) return;
    if (this.audioPlayedReported.has(messageId)) return;
    this.audioPlayedReported.add(messageId);
    try {
      await this.chatService.markAudioPlayed(messageId);
    } catch {
      // best-effort: drop the dedupe flag so a real retry can fire later
      this.audioPlayedReported.delete(messageId);
    }
  }

  async markRead() {
    const previous = this.unreadCount();
    if (previous === 0) return;
    const latestAdmin = this.messages().find((m) => m.sender === ChatSender.ADMIN);
    const until = latestAdmin?.createdAt;
    this.unreadCount.set(0);
    try {
      const res = await this.chatService.markRead(until);
      if (!res?.data && (res as any)?.error) {
        this.unreadCount.set(previous);
      }
    } catch {
      this.unreadCount.set(previous);
    }
  }
}
